import { AuthProvider } from '@refinedev/core';
import { UserDto, LoginResponseDto } from '@wellverse/shared-ts';
import {
  clearStoredSession,
  ensureFreshAccessToken,
  revokeServerSession,
} from './tokenManager';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:33000/api/v1';

export const authProvider: AuthProvider = {
  login: async ({ email, password }) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Type': 'CMS',
      },
      body: JSON.stringify({
        method: 'email',
        email,
        password,
        source: 'CMS',
      }),
    });

    if (!response.ok) {
      let message = 'Invalid email or password';
      try {
        const body = await response.json();
        if (body.message) message = body.message;
      } catch {
        // ignore parse failure
      }
      throw new Error(message);
    }

    const data: LoginResponseDto = await response.json();
    localStorage.setItem('access_token', data.accessToken);
    localStorage.setItem('refresh_token', data.refreshToken);
    localStorage.setItem('user', JSON.stringify(data.user));

    // Redirect trainer-role users directly to Scan to Tag
    const roles = data.user?.roles?.map((r) => r.name) ?? [];
    const isTrainerOnly = roles.length > 0 && roles.every((r) => r === 'trainer');

    return {
      success: true,
      redirectTo: isTrainerOnly ? '/tagging/scan' : '/',
    };
  },

  logout: async () => {
    await revokeServerSession(API_BASE_URL);
    clearStoredSession();
    return {
      success: true,
      redirectTo: '/login',
    };
  },

  check: async () => {
    let token = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');

    if (!token) {
      return {
        authenticated: false,
        redirectTo: '/login',
      };
    }

    if (refreshToken) {
      token = await ensureFreshAccessToken(API_BASE_URL);
    }
    if (!token) {
      clearStoredSession();
      return {
        authenticated: false,
        redirectTo: '/login',
      };
    }

    // Verify token by fetching current user
    try {
      const response = await fetch(`${API_BASE_URL}/users/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const user: UserDto = await response.json();
        localStorage.setItem('user', JSON.stringify(user));
        return {
          authenticated: true,
        };
      }

      clearStoredSession();
      return {
        authenticated: false,
        redirectTo: '/login',
      };
    } catch {
      return {
        authenticated: false,
        redirectTo: '/login',
      };
    }
  },

  getIdentity: async () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      return JSON.parse(userStr);
    }

    const token = localStorage.getItem('access_token');
    if (!token) return null;

    try {
      const response = await fetch(`${API_BASE_URL}/users/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const user: UserDto = await response.json();
        localStorage.setItem('user', JSON.stringify(user));
        return user;
      }
    } catch {
      // ignore
    }

    return null;
  },

  getPermissions: async () => {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;

    const user: UserDto = JSON.parse(userStr);
    // Return both legacy role and RBAC permissions
    const permissions = user.roles?.flatMap((role) =>
      role.permissions.map((p) => `${p.resource}:${p.action}`),
    ) ?? [];

    return {
      role: user.role,
      roles: user.roles?.map((r) => r.name) ?? [],
      permissions,
    };
  },

  onError: async (error: any) => {
    if (error?.status === 401 || error?.status === 403) {
      return {
        logout: true,
        redirectTo: '/login',
      };
    }

    return {};
  },
};
